import React, { FC, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { toString } from 'lodash'
import clsx from 'clsx'
import { FormInputLabel } from '../FormInputLabel'

interface FormTextField {
    name: string
    label?: string
    formControlClassName?: any
    formControl?: any
    className?: string
    formController?: any
    InputProps?: any
    minRows?: number
    maxRows?: number
    type?: 'text' | 'number' | 'password' | 'email' | 'tel' | 'search' | 'url' | 'color' | 'date' | 'time' | 'month' | 'range' | 'week' | 'subject' | 'textarea'
    defaultValue?: any
    fullWidth?: boolean
    multiline?: boolean
    disabled?: boolean
    readOnly?: boolean
    nullable?: boolean
    placeholder?: string
    color?: 'primary' | 'secondary',
    size?: 'lg' | 'md' | 'sm'
    step?: number
    min?: number
    max?: number
}

export const FormTextField: FC<
    FormTextField
> = ({
    readOnly = false,
    nullable,
    className,
    InputProps,
    multiline,
    fullWidth = true,
    formController,
    formControlClassName,
    formControl,
    size = 'md',
    ...textFieldProps
}) => {
    const { name, disabled, label, type, placeholder, min, max, defaultValue } = textFieldProps
    const _formContext = useFormContext()
    const formContext = formController ?? _formContext
    const {
        register,
        setValue,
        formState: { errors },
    } = formContext
    const thisError = errors[name]
    const thisErrorMessage = thisError ? toString(thisError?.message) : null
    const accentColor = 'accent-teal-500'

    // Tracked off the input's own onChange rather than react-hook-form's control/useWatch:
    // formController can be a hand-built stub (see FormTextField.spec.tsx) that doesn't
    // implement RHF's internals, and this only needs the raw DOM value anyway.
    // The native spinner silently clamps at min/max instead of firing a change event once
    // it's stuck there, so the only reliable signal is the value itself sitting on the boundary.
    const [liveValue, setLiveValue] = useState<string | number | undefined>(defaultValue)
    const numericValue = typeof liveValue === 'string' ? parseFloat(liveValue) : liveValue
    const atMin = type === 'number' && min !== undefined && numericValue === min
    const atMax = type === 'number' && max !== undefined && numericValue === max
    const boundaryMessage = thisErrorMessage
        ? null
        : atMin ? `Minimum is ${min}` : atMax ? `Maximum is ${max}` : null

    const registered = register(name)

    const componentProps = {
        id: name,
        type: multiline ? 'textarea' : type,
        placeholder: placeholder,
        ... multiline ? { multiline: 'true'} : {},
        'aria-multiline': multiline,
        ...textFieldProps,
        ...registered,
        onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setLiveValue(e.target.value)
            registered.onChange?.(e)
        },
        disabled,
        ...InputProps,
        className: clsx(
            'relative w-full px-2 bg-transparent ',
            'placeholder:text-slate-400 text-slate-700',
            'outline-hidden',
            !disabled && !readOnly && ' ',
            'text-'+size,
            className,
            size === 'lg' && 'top-1',
            size === 'md' && 'top-0.5',
            size === 'sm' && 'top-0.5',
            size === 'lg' && (thisErrorMessage ? 'input-md' : ''),
            size === 'md' && (thisErrorMessage ? 'input-sm' : ''),
            size === 'sm' && (thisErrorMessage ? 'input-sm' : ''),
            !multiline && size === 'lg' && (thisErrorMessage ? 'h-10 input-md' : 'h-12'),
            !multiline && size === 'md' && (thisErrorMessage ? 'input-sm h-8' : 'h-10'),
            !multiline && size === 'sm' && (thisErrorMessage ? 'input-sm h-7' : 'h-9'),
            multiline && size === 'lg' && (thisErrorMessage ? 'min-h-10 input-md' : 'min-h-12'),
            multiline && size === 'md' && (thisErrorMessage ? 'input-sm min-h-8' : 'min-h-10'),
            multiline && size === 'sm' && (thisErrorMessage ? 'input-sm min-h-7' : 'min-h-9'),
            (InputProps?.className ?? '')
        )
    }
    
    return (
        <div className={clsx(formControlClassName, fullWidth && 'w-full', 'relative my-1', accentColor)}>
            { label && <FormInputLabel
                label={label}
                inputId={name}
            /> }
            <div className={clsx(
                'border border-gray-300 transition duration-300 ease focus:border-teal-600 focus-within:border-teal-600 hover:border-teal-600  focus:outline-hidden shadow-xs focus:shadow rounded-md'
            )}>
                { multiline ? <div className='pt-1'>
                    <textarea {...componentProps} />
                </div> : <input {...componentProps} /> }
            </div>
            { thisErrorMessage && <div className={clsx(
                'text-red-500 ml-1 pt-0.5 flex h-2', 
                size === 'sm' ? 'text-xs' : 'text-sm'
            )}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 mr-0.5">
                    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" clipRule="evenodd" />
                </svg>
                { thisErrorMessage }
            </div> }
            { boundaryMessage && <div className={clsx(
                'text-slate-500 ml-1 pt-0.5',
                size === 'sm' ? 'text-xs' : 'text-sm'
            )}>
                { boundaryMessage }
            </div> }
        </div>
    )
}
